import type { KeyboardEvent } from 'react';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  CirclePlus,
  Loader2,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useNotification } from '@/components/notifications';
import HelpTooltip from '@/components/ui/help-tooltip';
import { DEFAULT_LLM_MODEL, useLLMModels } from '@/hooks/use-llm-models';
import {
  DatasetQueryErrorDetail,
  useDatasetQuery,
} from '@/hooks/use-dataset-query';
import { usePlaygroundConfig } from '@/hooks/use-playground-config';
import { getDatasetMetadata } from '@/services/data-sources/get-dataset-metadata';

import { DatasetQueryResults } from './dataset-query-results';

interface LLMPlaygroundCardProps {
  orgId: string;
  collectionId: string;
  datasetId: string;
}

const buildErrorMessage = (detail: DatasetQueryErrorDetail | null) => {
  if (!detail) return null;
  const messageParts = [detail.message];
  if (detail.error_message && detail.error_message !== detail.message) {
    messageParts.push(detail.error_message);
  }
  if (detail.reason) {
    messageParts.push(detail.reason);
  }
  return messageParts.filter(Boolean).join(' — ');
};

export const LLMPlaygroundCard = ({
  orgId,
  collectionId,
  datasetId,
}: LLMPlaygroundCardProps) => {
  const notification = useNotification();
  const {
    options,
    isLoading: isFetchingModels,
    hasFetched: hasModelsFetched,
    error: modelsError,
  } = useLLMModels();
  const {
    config: playgroundConfig,
    isLoading: isPlaygroundLoading,
    isSaving: isPlaygroundSaving,
    error: playgroundError,
    hasFetched: hasPlaygroundFetched,
    savePrompts,
    saveDefaultModel,
  } = usePlaygroundConfig(orgId, collectionId);

  const [question, setQuestion] = useState('');
  const [includeSql, setIncludeSql] = useState(false);
  const [model, setModel] = useState(DEFAULT_LLM_MODEL);
  const [sqlHistory, setSqlHistory] = useState<string | null>(null);
  const [newPrompt, setNewPrompt] = useState('');
  const [prompts, setPrompts] = useState<string[]>([]);
  const [hasShownModelWarning, setHasShownModelWarning] = useState(false);
  const [qualityWarning, setQualityWarning] = useState<{
    loading: boolean;
    error: string | null;
    unresolved: string[];
  }>({ loading: true, error: null, unresolved: [] });
  const [isPromptsExpanded, setIsPromptsExpanded] = useState(true);

  const { runQuery, data, error, isLoading } = useDatasetQuery({
    orgId,
    collectionId,
    datasetId,
  });

  const errorMessage = useMemo(() => buildErrorMessage(error), [error]);

  const groupedOptions = useMemo(() => {
    const groups: {
      label: string;
      options: { label: string; value: string }[];
    }[] = [
      {
        label: 'Preferences',
        options: [{ label: 'None (use recommended model)', value: '__none__' }],
      },
    ];

    if (options.openai.length) {
      groups.push({ label: 'OpenAI Models', options: options.openai });
    }

    if (options.anthropic.length) {
      groups.push({ label: 'Anthropic Models', options: options.anthropic });
    }

    return groups;
  }, [options]);

  const flatOptions = useMemo(
    () => groupedOptions.flatMap((group) => group.options),
    [groupedOptions],
  );

  const availableModelOptions = useMemo(
    () => flatOptions.filter((option) => option.value !== '__none__'),
    [flatOptions],
  );

  useEffect(() => {
    setPrompts(playgroundConfig.prompts);
  }, [playgroundConfig.prompts]);

  useEffect(() => {
    if (!collectionId || !datasetId) {
      setQualityWarning({ loading: false, error: null, unresolved: [] });
      return;
    }

    let cancelled = false;

    const loadQuality = async () => {
      try {
        setQualityWarning((prev) => ({ ...prev, loading: true }));
        const result = await getDatasetMetadata(collectionId, datasetId);

        if ('detail' in result) {
          if (!cancelled) {
            setQualityWarning({
              loading: false,
              error: result.detail || 'Failed to load data-quality status.',
              unresolved: [],
            });
          }
          return;
        }

        const unresolved =
          result.tables?.filter(
            (table) => table.quality?.requires_resolution,
          ) ?? [];

        const unresolvedNames = unresolved.map(
          (table, index) =>
            table.filename ||
            table.table ||
            table.s3_key?.split('/').pop() ||
            table.source_key?.split('/').pop() ||
            `Table ${index + 1}`,
        );

        if (!cancelled) {
          setQualityWarning({
            loading: false,
            error: null,
            unresolved: unresolvedNames,
          });
        }
      } catch (error) {
        console.error('Failed to fetch dataset metadata:', error);
        if (!cancelled) {
          setQualityWarning({
            loading: false,
            error: 'Failed to load data-quality status.',
            unresolved: [],
          });
        }
      }
    };

    void loadQuality();

    return () => {
      cancelled = true;
    };
  }, [collectionId, datasetId]);

  useEffect(() => {
    // Wait until both models and playground config have been fetched from API
    if (!hasModelsFetched || !hasPlaygroundFetched) {
      return;
    }

    const storedDefault = playgroundConfig.default_model;
    const hasStored =
      storedDefault !== null &&
      availableModelOptions.some((option) => option.value === storedDefault);

    // Only show warning once per component mount, and only if model is truly unavailable
    if (
      storedDefault &&
      !hasStored &&
      availableModelOptions.length &&
      !hasShownModelWarning
    ) {
      notification.warning(
        'Saved default model is unavailable. Using an available model instead.',
      );
      setHasShownModelWarning(true);
    }

    const resolvedValue = (() => {
      if (storedDefault && hasStored) {
        return storedDefault;
      }

      if (storedDefault === null) {
        return '__none__';
      }

      if (availableModelOptions.length) {
        const defaultCandidate = availableModelOptions.find(
          (option) => option.value === DEFAULT_LLM_MODEL,
        );
        if (defaultCandidate) {
          return defaultCandidate.value;
        }

        return availableModelOptions[0].value;
      }

      return '__none__';
    })();

    setModel((current) =>
      current === resolvedValue ? current : resolvedValue,
    );
  }, [
    availableModelOptions,
    hasModelsFetched,
    hasPlaygroundFetched,
    hasShownModelWarning,
    notification,
    playgroundConfig.default_model,
  ]);

  const selectedDefaultValue = useMemo(
    () => (model === '__none__' ? null : model),
    [model],
  );

  const isCurrentDefault =
    selectedDefaultValue === playgroundConfig.default_model;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!question.trim() || isLoading) return;

    const trimmedQuestion = question.trim();
    const modelOverride =
      model === '__none__'
        ? undefined
        : model !== DEFAULT_LLM_MODEL
          ? model
          : undefined;

    await runQuery({
      question: trimmedQuestion,
      include_sql: includeSql,
      ...(modelOverride ? { model: modelOverride } : {}),
    });
  };

  useEffect(() => {
    if (data?.sql) {
      setSqlHistory(data.sql);
    }
  }, [data?.sql]);

  const handlePromptSelect = useCallback((prompt: string) => {
    setQuestion(prompt);
  }, []);

  const handleAddPrompt = useCallback(async () => {
    const trimmed = newPrompt.trim();
    if (!trimmed) {
      notification.warning('Prompt cannot be empty.');
      return;
    }

    if (
      prompts.some((prompt) => prompt.toLowerCase() === trimmed.toLowerCase())
    ) {
      notification.warning('Prompt already exists.');
      return;
    }

    try {
      await savePrompts([...prompts, trimmed]);
      setNewPrompt('');
      notification.success('Prompt added.');
    } catch (addError) {
      const message =
        addError instanceof Error ? addError.message : 'Failed to add prompt.';
      notification.error(message);
    }
  }, [newPrompt, notification, prompts, savePrompts]);

  const handlePromptRemove = useCallback(
    async (promptToRemove: string) => {
      const updatedPrompts = prompts.filter(
        (prompt) => prompt !== promptToRemove,
      );

      try {
        await savePrompts(updatedPrompts);
        notification.success('Prompt removed.');
      } catch (removeError) {
        const message =
          removeError instanceof Error
            ? removeError.message
            : 'Failed to remove prompt.';
        notification.error(message);
      }
    },
    [notification, prompts, savePrompts],
  );

  const handleModelSelect = useCallback((value: string) => {
    setModel((current) => (current === value ? current : value));
  }, []);

  const handleSetDefaultModel = useCallback(async () => {
    if (isCurrentDefault) {
      return;
    }

    try {
      await saveDefaultModel(selectedDefaultValue);
      notification.success(
        selectedDefaultValue
          ? 'Default model updated.'
          : 'Default model cleared.',
      );
    } catch (updateError) {
      const message =
        updateError instanceof Error
          ? updateError.message
          : 'Failed to update default model.';
      notification.error(message);
    }
  }, [isCurrentDefault, notification, saveDefaultModel, selectedDefaultValue]);

  const handlePromptInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void handleAddPrompt();
      }
    },
    [handleAddPrompt],
  );

  const isPlaygroundBusy = isPlaygroundLoading || isPlaygroundSaving;
  const showQualityBanner =
    !qualityWarning.loading && qualityWarning.unresolved.length > 0;

  return (
    <Card className='shadow-sm border-dashed'>
      <CardHeader>
        <CardTitle className='text-xl'>LLM Playground</CardTitle>
        <p className='text-sm text-muted-foreground'>
          Ask questions about this dataset. Queries execute live against the
          structured data and return tabular results.
        </p>
      </CardHeader>
      <CardContent className='space-y-6'>
        {showQualityBanner && (
          <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
            <div className='flex items-start gap-2'>
              <AlertTriangle className='h-4 w-4 mt-0.5 shrink-0' />
              <div className='space-y-1'>
                <p className='font-medium'>
                  We’re skipping tables with unresolved data-quality issues.
                </p>
                {qualityWarning.unresolved.length > 0 && (
                  <p className='text-xs text-amber-900/80'>
                    Affected tables: {qualityWarning.unresolved.join(', ')}.
                  </p>
                )}
                <a
                  href='#data-quality-summary'
                  className='text-xs text-primary underline'
                >
                  Resolve issues to unlock full results
                </a>
              </div>
            </div>
          </div>
        )}
        {!showQualityBanner &&
          qualityWarning.error &&
          !qualityWarning.loading && (
            <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900'>
              <div className='flex items-start gap-2'>
                <AlertTriangle className='h-4 w-4 mt-0.5 shrink-0' />
                <div>
                  <p className='font-medium'>Couldn’t check data quality.</p>
                  <p>{qualityWarning.error}</p>
                </div>
              </div>
            </div>
          )}
        <Collapsible
          open={isPromptsExpanded}
          onOpenChange={setIsPromptsExpanded}
          className='space-y-3'
        >
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Label className='text-sm font-medium'>Saved prompts</Label>
              <HelpTooltip label='Saved prompts help'>
                <div className='space-y-1 text-sm'>
                  Saved prompts keep quick-start questions for this dataset.
                  Click a prompt to use it in the question box below.
                </div>
              </HelpTooltip>
              {prompts.length > 0 && (
                <span className='text-xs text-muted-foreground'>
                  ({prompts.length})
                </span>
              )}
            </div>
            {prompts.length > 0 && (
              <CollapsibleTrigger asChild>
                <Button
                  variant='outline'
                  size='sm'
                  className='h-8 px-2 gap-1'
                  disabled={isPlaygroundBusy}
                >
                  {isPromptsExpanded ? (
                    <>
                      <span className='text-xs'>Hide</span>
                      <ChevronUp className='h-4 w-4' />
                    </>
                  ) : (
                    <>
                      <span className='text-xs'>Show</span>
                      <ChevronDown className='h-4 w-4' />
                    </>
                  )}
                  <span className='sr-only'>
                    {isPromptsExpanded ? 'Collapse' : 'Expand'} saved prompts
                  </span>
                </Button>
              </CollapsibleTrigger>
            )}
          </div>

          <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
            <Textarea
              value={newPrompt}
              onChange={(event) => setNewPrompt(event.target.value)}
              onKeyDown={handlePromptInputKeyDown}
              placeholder='Add a new prompt'
              disabled={isPlaygroundBusy}
              className='flex-1 resize-y min-h-[2.5rem]'
              rows={1}
            />
            <Button
              type='button'
              size='sm'
              onClick={() => void handleAddPrompt()}
              disabled={isPlaygroundBusy}
              className='sm:self-end'
            >
              <CirclePlus className='mr-1 h-4 w-4' />
              Add
            </Button>
          </div>

          <CollapsibleContent className='space-y-2'>
            {isPlaygroundLoading ? (
              <p className='text-xs text-muted-foreground'>
                Loading saved prompts…
              </p>
            ) : prompts.length ? (
              <div className='space-y-2 max-h-[300px] overflow-y-auto pr-2'>
                {prompts.map((prompt, index) => (
                  <div
                    key={`${prompt}-${index}`}
                    className='group flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-sm transition hover:border-primary/40 hover:bg-muted/50'
                  >
                    <button
                      type='button'
                      onClick={() => handlePromptSelect(prompt)}
                      className='flex-1 text-left leading-relaxed text-foreground transition hover:text-primary'
                      disabled={isPlaygroundBusy}
                    >
                      {prompt}
                    </button>
                    <button
                      type='button'
                      onClick={() => void handlePromptRemove(prompt)}
                      className='shrink-0 rounded p-1 text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive'
                      aria-label={`Remove prompt`}
                      disabled={isPlaygroundBusy}
                    >
                      <X className='h-4 w-4' />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className='text-xs text-muted-foreground'>
                No saved prompts yet. Add one above to create quick selections.
              </p>
            )}
          </CollapsibleContent>
        </Collapsible>
        {playgroundError && (
          <p className='text-xs text-destructive'>{playgroundError}</p>
        )}

        <form className='space-y-4' onSubmit={handleSubmit}>
          <div className='space-y-2'>
            <Label htmlFor='llm-question' className='label-required'>
              Question
            </Label>
            <Textarea
              id='llm-question'
              value={question}
              rows={4}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder='E.g., "How many students graduated in 2023?"'
              disabled={isLoading}
            />
          </div>

          <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
            <label className='flex items-center gap-2 text-sm sm:pt-2'>
              <Checkbox
                checked={includeSql}
                onCheckedChange={(value) => setIncludeSql(value === true)}
                disabled={isLoading}
              />
              Include generated SQL
            </label>

            <div className='space-y-2 sm:max-w-md'>
              <Label className='text-sm'>Model</Label>
              <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
                <Select
                  value={model}
                  onValueChange={handleModelSelect}
                  disabled={isLoading || isFetchingModels || isPlaygroundBusy}
                >
                  <SelectTrigger className='w-full sm:w-[240px]'>
                    <SelectValue placeholder='Loading models…' />
                  </SelectTrigger>
                  <SelectContent>
                    {groupedOptions.map((group) => (
                      <SelectGroup key={group.label}>
                        <SelectLabel>{group.label}</SelectLabel>
                        {group.options.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type='button'
                  size='sm'
                  onClick={() => void handleSetDefaultModel()}
                  disabled={isPlaygroundBusy || isCurrentDefault}
                  className='w-full sm:w-auto'
                >
                  Set as default
                </Button>
              </div>
              {isFetchingModels && !modelsError && (
                <p className='text-xs text-muted-foreground'>Loading models…</p>
              )}
              {modelsError && (
                <p className='text-xs text-muted-foreground'>
                  Showing cached models — {modelsError}
                </p>
              )}
            </div>
          </div>

          <div className='flex justify-end'>
            <Button
              type='submit'
              disabled={!question.trim() || isLoading}
              className='min-w-[140px]'
            >
              {isLoading ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Running…
                </>
              ) : (
                'Run query'
              )}
            </Button>
          </div>
        </form>

        {errorMessage && (
          <div className='rounded-md border border-destructive/40 bg-destructive/10 p-4 space-y-2'>
            <p className='text-sm font-medium text-destructive'>Query failed</p>
            <p className='text-sm text-destructive'>{errorMessage}</p>
            {error?.sql && (
              <div className='space-y-1'>
                <Label className='text-xs text-muted-foreground'>SQL</Label>
                <pre className='rounded-md border bg-muted/50 p-2 text-xs whitespace-pre-wrap text-foreground'>
                  {error.sql}
                </pre>
              </div>
            )}
          </div>
        )}

        {data && <DatasetQueryResults result={data} lastSql={sqlHistory} />}
      </CardContent>
    </Card>
  );
};
